import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const IndustriesCarousel = ({ industries }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      skipSnaps: false,
      slidesToScroll: 1
    },
    [Autoplay({ delay: 3000, stopOnInteraction: false })]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      {/* Carousel Container */}
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex items-stretch -mx-3">
          {industries.map((industry) => (
            <div
              key={industry.name}
              className="flex-[0_0_100%] min-w-0 sm:flex-[0_0_50%] md:flex-[0_0_33.333%] lg:flex-[0_0_25%]"
            >
              <div className="mx-3 h-full">
                <div className="group relative h-full flex flex-col">
                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/15 to-blue-500/15 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500" />
                  
                  {/* Card */}
                  <div className="relative flex-1 flex flex-col bg-white/70 backdrop-blur-xl border border-cyan-500/20 rounded-2xl hover:border-cyan-500/40 transition-all duration-500 transform group-hover:scale-105 group-hover:shadow-xl group-hover:shadow-cyan-500/10">
                    {/* Image Container - Fixed aspect ratio with overflow hidden */}
                    <div className="relative w-full overflow-hidden rounded-t-2xl" style={{ paddingBottom: '100%' }}>
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10 opacity-40 group-hover:opacity-20 transition-opacity duration-500" />
                      
                      <img
                        src={industry.image}
                        alt={industry.name}
                        className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      
                      {/* Shine Effect on Hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                    </div>
                    
                    {/* Text - Fixed height */}
                    <div className="p-5 text-center flex items-center justify-center" style={{ minHeight: '80px' }}>
                      <h3 className="text-gray-800 font-semibold text-lg group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-cyan-600 group-hover:to-blue-600 transition-all duration-300">
                        {industry.name}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-20 group"
        aria-label="Previous slide"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="relative bg-white/90 backdrop-blur-xl border border-cyan-500/30 p-2 sm:p-3 rounded-full hover:border-cyan-500/60 transition-all group-hover:scale-110 shadow-lg">
          <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-600" />
        </div>
      </button>

      <button
        onClick={scrollNext}
        className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-20 group"
        aria-label="Next slide"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="relative bg-white/90 backdrop-blur-xl border border-cyan-500/30 p-2 sm:p-3 rounded-full hover:border-cyan-500/60 transition-all group-hover:scale-110 shadow-lg">
          <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-600" />
        </div>
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-8">
        {Array.from({ length: Math.ceil(industries.length / 4) }).map((_, index) => (
          <button
            key={`dot-${index}`}
            onClick={() => emblaApi?.scrollTo(index * 4)}
            className={`transition-all duration-300 ${
              Math.floor(selectedIndex / 4) === index
                ? 'w-8 bg-gradient-to-r from-cyan-500 to-blue-500'
                : 'w-2 bg-gray-300 hover:bg-cyan-500/50'
            } h-2 rounded-full`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default IndustriesCarousel;
